import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { BudgetVsActualChart } from './charts/budget-vs-actual-chart';
import { BurnRateChart } from './charts/burn-rate-chart';
import { AlertTriangle, TrendingUp, TrendingDown } from 'lucide-react';
import { formatCurrency, formatPercent, getVarianceColor } from '@/lib/budget-utils';
import type { AllBudgetsView as AllBudgetsData } from '@/types/budget.types';

interface AllBudgetsViewProps {
  data: AllBudgetsData;
}

export function AllBudgetsView({ data }: AllBudgetsViewProps) {
  // Add extensive debugging
  console.log('[AllBudgetsView] Received data:', data);
  console.log('[AllBudgetsView] Data type:', typeof data);
  console.log('[AllBudgetsView] Has profitability:', data?.profitability !== undefined);
  console.log('[AllBudgetsView] Has burnRateData:', data?.burnRateData !== undefined);
  console.log('[AllBudgetsView] Has forecast:', data?.forecast !== undefined);

  if (!data) {
    console.error('[AllBudgetsView] Data is null or undefined');
    return (
      <div className="flex items-center justify-center h-96">
        <p className="text-muted-foreground">No budget data available (data is null)</p>
      </div>
    );
  }

  if (!data.profitability) {
    console.error('[AllBudgetsView] Missing profitability data');
    return (
      <div className="flex items-center justify-center h-96">
        <p className="text-muted-foreground">No profitability data available</p>
      </div>
    );
  }

  if (!data.burnRateData) {
    console.error('[AllBudgetsView] Missing burnRateData');
    return (
      <div className="flex items-center justify-center h-96">
        <p className="text-muted-foreground">No burn rate data available</p>
      </div>
    );
  }

  if (!data.forecast) {
    console.error('[AllBudgetsView] Missing forecast data');
    return (
      <div className="flex items-center justify-center h-96">
        <p className="text-muted-foreground">No forecast data available</p>
      </div>
    );
  }

  try {
    const { profitability, burnRateData, forecast } = data;
    console.log('[AllBudgetsView] Successfully destructured data');

  // Create chart data from profitability data
  const budgetVsActualData = [
    {
      category: 'Revenue',
      budget: profitability.revenue.budget,
      actual: profitability.revenue.actual,
    },
    {
      category: 'Direct Costs',
      budget: profitability.directCosts.budget,
      actual: profitability.directCosts.actual,
    },
    {
      category: 'Gross Profit',
      budget: profitability.grossProfit.budget,
      actual: profitability.grossProfit.actual,
    },
    {
      category: 'Overhead',
      budget: profitability.overhead.budget,
      actual: profitability.overhead.actual,
    },
    {
      category: 'Net Profit',
      budget: profitability.netProfit.budget,
      actual: profitability.netProfit.actual,
    },
  ];

  return (
    <div className="space-y-6">
      {/* Profitability Summary */}
      <Card>
        <CardHeader>
          <CardTitle>Profitability Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Revenue */}
            <div className="grid grid-cols-4 gap-4 items-center">
              <div className="font-medium">Revenue</div>
              <div className="text-right">{formatCurrency(profitability.revenue.budget)}</div>
              <div className="text-right">{formatCurrency(profitability.revenue.actual)}</div>
              <div className={`text-right ${getVarianceColor(profitability.revenue.variance)}`}>
                {formatCurrency(profitability.revenue.variance)} rem
              </div>
            </div>

            {/* Direct Costs */}
            <div className="grid grid-cols-4 gap-4 items-center">
              <div className="font-medium">(-) Direct Costs</div>
              <div className="text-right">{formatCurrency(profitability.directCosts.budget)}</div>
              <div className="text-right">{formatCurrency(profitability.directCosts.actual)}</div>
              <div className={`text-right ${getVarianceColor(profitability.directCosts.variance)}`}>
                {formatCurrency(profitability.directCosts.variance)} rem
              </div>
            </div>

            <Separator />

            {/* Gross Profit */}
            <div className="grid grid-cols-4 gap-4 items-center font-semibold">
              <div>Gross Profit</div>
              <div className="text-right">{formatCurrency(profitability.grossProfit.budget)}</div>
              <div className="text-right">{formatCurrency(profitability.grossProfit.actual)}</div>
              <div className={`text-right ${getVarianceColor(profitability.grossProfit.variance)}`}>
                {formatPercent(profitability.grossProfit.variance / profitability.grossProfit.budget)}
              </div>
            </div>

            {/* Gross Margin */}
            <div className="grid grid-cols-4 gap-4 items-center text-sm text-muted-foreground">
              <div>Gross Margin</div>
              <div className="text-right">{formatPercent(profitability.grossMargin.budget)}</div>
              <div className="text-right">{formatPercent(profitability.grossMargin.actual)}</div>
              <div className={`text-right ${getVarianceColor(profitability.grossMargin.variance)}`}>
                {formatPercent(profitability.grossMargin.variance)}
              </div>
            </div>

            {/* Overhead */}
            <div className="grid grid-cols-4 gap-4 items-center">
              <div className="font-medium">(-) Overhead Allocation</div>
              <div className="text-right">{formatCurrency(profitability.overhead.budget)}</div>
              <div className="text-right">{formatCurrency(profitability.overhead.actual)}</div>
              <div className={`text-right ${getVarianceColor(profitability.overhead.variance)}`}>
                {formatCurrency(profitability.overhead.variance)} rem
              </div>
            </div>

            <Separator />

            {/* Net Profit */}
            <div className="grid grid-cols-4 gap-4 items-center font-bold">
              <div>Net Profit</div>
              <div className="text-right">{formatCurrency(profitability.netProfit.budget)}</div>
              <div className="text-right">{formatCurrency(profitability.netProfit.actual)}</div>
              <div className={`text-right ${getVarianceColor(profitability.netProfit.variance)}`}>
                {formatPercent(profitability.netProfit.variance / profitability.netProfit.budget)}
              </div>
            </div>

            {/* Net Margin */}
            <div className="grid grid-cols-4 gap-4 items-center text-sm text-muted-foreground">
              <div>Net Margin</div>
              <div className="text-right">{formatPercent(profitability.netMargin.budget)}</div>
              <div className="text-right">{formatPercent(profitability.netMargin.actual)}</div>
              <div className={`text-right ${getVarianceColor(profitability.netMargin.variance)}`}>
                {formatPercent(profitability.netMargin.variance)}
              </div>
            </div>

            {/* Column Headers */}
            <div className="grid grid-cols-4 gap-4 text-xs text-muted-foreground pt-2 border-t">
              <div></div>
              <div className="text-right">Budget</div>
              <div className="text-right">Actual</div>
              <div className="text-right">Variance</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Budget vs Actual</CardTitle>
          </CardHeader>
          <CardContent>
            <BudgetVsActualChart data={budgetVsActualData} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Burn Rate</CardTitle>
          </CardHeader>
          <CardContent>
            <BurnRateChart data={burnRateData} />
          </CardContent>
        </Card>
      </div>

      {/* Forecast to Complete */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Forecast to Complete
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Based on current burn rate:
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Projected Revenue */}
              <div className="space-y-1">
                <div className="text-sm font-medium">Projected Final Revenue</div>
                <div className="text-2xl font-bold">{formatCurrency(forecast.projectedRevenue)}</div>
                <div className={`text-sm flex items-center gap-1 ${getVarianceColor(forecast.revenueVariance)}`}>
                  {forecast.revenueVariance >= 0 ? (
                    <TrendingUp className="h-3 w-3" />
                  ) : (
                    <TrendingDown className="h-3 w-3" />
                  )}
                  {formatCurrency(Math.abs(forecast.revenueVariance))} {forecast.revenueVariance >= 0 ? 'over' : 'under'} budget
                </div>
              </div>

              {/* Projected Cost */}
              <div className="space-y-1">
                <div className="text-sm font-medium">Projected Final Cost</div>
                <div className="text-2xl font-bold">{formatCurrency(forecast.projectedCost)}</div>
                <div className={`text-sm flex items-center gap-1 ${getVarianceColor(-forecast.costVariance)}`}>
                  {forecast.costVariance <= 0 ? (
                    <TrendingUp className="h-3 w-3" />
                  ) : (
                    <TrendingDown className="h-3 w-3" />
                  )}
                  {formatCurrency(Math.abs(forecast.costVariance))} {forecast.costVariance > 0 ? 'over' : 'under'} budget
                </div>
              </div>

              {/* Projected Margin */}
              <div className="space-y-1">
                <div className="text-sm font-medium">Projected Final Margin</div>
                <div className="text-2xl font-bold">{formatPercent(forecast.projectedMargin)}</div>
                <div className={`text-sm flex items-center gap-1 ${getVarianceColor(forecast.marginVariance)}`}>
                  {forecast.marginVariance >= 0 ? (
                    <TrendingUp className="h-3 w-3" />
                  ) : (
                    <TrendingDown className="h-3 w-3" />
                  )}
                  {formatPercent(Math.abs(forecast.marginVariance))} {forecast.marginVariance >= 0 ? 'above' : 'below'} target
                </div>
              </div>
            </div>

            {/* Warning Message */}
            {forecast.warnings && forecast.warnings.length > 0 && (
              <div className="mt-4 p-4 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-500 mt-0.5" />
                  <div className="space-y-1">
                    {forecast.warnings.map((warning: string, index: number) => (
                      <p key={index} className="text-sm text-amber-900 dark:text-amber-100">
                        {warning}
                      </p>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
  } catch (error) {
    console.error('[AllBudgetsView] Error rendering component:', error);
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center space-y-2">
          <p className="text-muted-foreground">Error loading budget view</p>
          <p className="text-sm text-red-500">{error instanceof Error ? error.message : 'Unknown error'}</p>
        </div>
      </div>
    );
  }
}
